import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { FieldLabel } from '@components/framework/formfield/FieldLabel.tsx';

interface ComponentProps {
  helpText?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  errorMessage?: string;
  children: ReactElement<{
    value: any;
    values: any[];
    required: boolean;
    readOnly: boolean;
    disabled: boolean;
    style: { [key: string]: any }
  }>;
  actionButtons?: ReactElement;
  buttonsMode?: 'actionButton' | 'iconButton';
  width?: string;
  skipCurrencyCssMode?: boolean;
}

export const Field: React.FC<ComponentProps> = ({
                                                  helpText,
                                                  label,
                                                  children,
                                                  errorMessage,
                                                  required = false,
                                                  readOnly,
                                                  disabled,
                                                  actionButtons,
                                                  width,
                                                  buttonsMode = 'iconButton',
                                                  skipCurrencyCssMode = false,
                                                }) => {
  const hasValue =
    (isNotNullOrUndefined(children.props.values) && Array.isArray(children.props.values)
      ? children.props.values.length > 0
      : isNotNullOrUndefined(children.props.value) && `${children.props.value}`.trim() !== '');

  const convertToMode = (errorMessage: string, readOnly: boolean, disabled: boolean, required: boolean): 'error' | 'disabled' | 'readOnly' | 'normal' | 'required' => {
    if (errorMessage) {
      return 'error';
    }
    if (disabled) {
      return 'disabled';
    }
    if (readOnly) {
      return 'readOnly';
    }
    if (required && !hasValue) {
      return 'required';
    }
    return 'normal';
  };

  const mode = convertToMode(errorMessage, readOnly, disabled, required);
  return (
    <>
      <Content mode={mode} className={errorMessage ? 'hasError' : null} width={width} skipCurrencyCssMode={skipCurrencyCssMode}>
        {label && (
          <FieldLabel
            label={label}
            helpText={helpText}
            mode={mode}
          />
        )}
        <div className={''}>
          <ContentInner>
            {React.cloneElement(children, {
              readOnly,
              required: required && !hasValue,
              disabled,
              style: { width: '100%' },
            })}
            {isNotNullOrUndefined(actionButtons) && (
              <div
                className={twMerge(
                  clsx(
                    '',
                    {
                      'flex flex-row gap-2 ml-2 items-center': buttonsMode === 'iconButton',
                    },
                    {
                      '': buttonsMode === 'actionButton',
                    },
                  ),
                )}
              >
                {actionButtons}
              </div>
            )}
          </ContentInner>
        </div>
        {errorMessage && <div className={'text-complementary-error text-[10px]'}>{errorMessage}</div>}
      </Content>
    </>
  );
};

const ContentInner = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  min-height: 40px;
`;

const Content = styled.div<{
  mode: 'error' | 'disabled' | 'readOnly' | 'normal' | 'required';
  width?: string;
  skipCurrencyCssMode: boolean
}>`
  display: flex;
  flex-direction: column;

  ${props =>
      props.width &&
      css`
        width: ${props.width};
      `}
  span.p-inputtext {
    border: none !important;
  }

  input,
  textarea,
  div.p-inputwrapper,
  div.r-input-wrapper {
    border: 1px solid var(--ringil3-content-secondary);
    border-radius: var(--ringil-border-radius);
  }

  input.p-bottom-dotted-border {
    border: none !important;
    border-bottom: 1px dotted var(--ringil3-content-secondary) !important;
  }

  input.p-no-border {
    border: none !important;
  }

  span.p-inputtext {
    border: 1px solid var(--ringil3-content-secondary);
    border-radius: var(--ringil-border-radius);
  }

  ${props =>
      props.mode === 'error' &&
      css`
        ${!props.skipCurrencyCssMode ? `
        div.p-inputwrapper,
        ` : ''}
        input,
        textarea,
        input:focus,
        div.r-input-wrapper {
          border: 1px solid var(--ringil3-complementary-error-color);
          color: var(--ringil3-complementary-error-color);
          background-color: var(--complementary-errormedium) !important;
        }

        div.r-input-wrapper {
          background-color: var(--complementary-errormedium) !important;
        }

        input.p-bottom-dotted-border {
          border: none !important;
          border-bottom: 1px dotted var(--ringil3-complementary-error-color) !important;
          background-color: var(--complementary-errormedium) !important;
        }

        input.p-no-border {
          border: none !important;
          background-color: var(--complementary-errormedium) !important;
        }

        .p-calendar .p-inputtext {
          border: 1px solid var(--ringil3-complementary-error-color) !important;
          color: var(--ringil3-complementary-error-color) !important;
          background-color: var(--complementary-errormedium) !important;
        }

        ${!props.skipCurrencyCssMode ? `
          span.p-inputtext {
            color: var(--ringil3-complementary-error-color);
            background-color: var(--complementary-errormedium) !important;
          }
        ` : ''}
      `}

  ${props =>
      props.mode === 'required' &&
      css`
        ${!props.skipCurrencyCssMode ? `
        div.p-inputwrapper,
        ` : ''}
        input,
        textarea,
        div.r-input-wrapper {
          border: 1px solid var(--ringil3-content-secondary) !important;
          background-color: var(--ringil3-complementary-warning-medium) !important;
        }

        input.p-bottom-dotted-border {
          border: none !important;
          border-bottom: 1px dotted var(--ringil3-content-secondary) !important;
          background-color: var(--ringil3-complementary-warning-medium) !important;
        }

        input.p-no-border {
          border: none !important;
          background-color: var(--ringil3-complementary-warning-medium) !important;
        }

        .p-calendar .p-inputtext {
          border: 1px solid var(--ringil3-content-secondary) !important;
          background-color: var(--ringil3-complementary-warning-medium) !important;
        }

        ${!props.skipCurrencyCssMode ? `
          span.p-inputtext {
            border: 1px solid var(--ringil3-content-secondary);
            background-color: var(--ringil3-complementary-warning-medium);
          }
        ` : ''}
      `}

  ${props =>
      props.mode === 'disabled' &&
      css`
        ${!props.skipCurrencyCssMode ? `
        div.p-inputwrapper,
        ` : ''}
        input,
        textarea {
          border: 1px solid var(--ringil3-content-tertiary-color);
          color: var(--ringil3-content-tertiary-color);
        }

        .p-calendar .p-inputtext { {
          border: 1px solid var(--ringil3-content-tertiary-color) !important;
          color: var(--ringil3-content-tertiary-color) !important;
        }

        ${!props.skipCurrencyCssMode ? `
          span.p-inputtext {
            color: var(--ringil3-content-tertiary-color);
          }
        ` : ''}
      `}

  ${props =>
      props.mode === 'readOnly' &&
      css`
        ${!props.skipCurrencyCssMode ? `
        div.p-inputwrapper,
        ` : ''}
        input,
        input:focus,
        textarea {
          border: 1px solid var(--ringil3-content-tertiary-color);
          color: var(--ringil3-content-tertiary-color);
        }
      `}
`;
